import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from '@/components/ui/sidebar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { NavItem, NavSubItem } from './app-sidebar';
import { activeNavUrl, navMatchScore, type NavLocation } from './nav-active-match';

/**
 * Reads nav collapse state from localStorage, tolerating blocked storage
 * (e.g. block-all-cookies) by falling back to null so callers use their
 * own default.
 */
function readStoredNavState(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

/**
 * Persists nav collapse state to localStorage, silently no-op'ing when
 * storage is blocked — state simply stays session-local in that case.
 */
function writeStoredNavState(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        /* ignore blocked-storage failures — state stays session-local */
    }
}

/**
 * Single localStorage key for the one open nav group.
 *
 * Deliberately NOT the former per-group `nav-collapsed:<key>` scheme: that shape
 * can represent several groups open at once, so hydrating from it would restore
 * the exact multi-open state the accordion exists to prevent. `NavSection` still
 * owns `nav-collapsed:` for its own (independent) fold state.
 */
const OPEN_GROUP_STORAGE_KEY = 'nav-open-group';

/**
 * The shape shared by a top-level collapsible group and one nested inside a
 * labelled section. Both render through {@link NavGroupItem}, so the accordion,
 * route auto-open and icon-collapsed dropdown behave identically at either
 * depth — the only difference is who lists them.
 */
interface INavGroupLike {
    title: string;
    icon?: NavItem['icon'];
    collapseKey?: string;
    items?: NavSubItem[];
}

/** Stable identity for a collapsible group — also its persisted value. */
function groupKeyOf(item: INavGroupLike): string {
    return item.collapseKey ?? item.title;
}

function isGroupItem(item: NavItem): boolean {
    return item.isGroup === true && (item.items?.length ?? 0) > 0;
}

/** A section child that folds children of its own rather than navigating. */
function isNestedGroup(sub: NavSubItem): boolean {
    return (sub.items?.length ?? 0) > 0;
}

/**
 * Whether `url` is the row that OWNS `navLocation`, judged against the rows beside it.
 *
 * A bare prefix match lit every ancestor: on `/opportunities/reports` the "All"
 * row (`/opportunities`) matched the prefix branch while "Reports" matched
 * exactly, so both highlighted at once. Deepest match wins, so an ancestor yields
 * to whichever sibling owns the route — while still covering the detail pages
 * that no sibling claims.
 */
function isNavRowActive(
    navLocation: NavLocation,
    url: string | undefined,
    siblings: readonly { readonly url?: string }[],
): boolean {
    const score = navMatchScore(navLocation, url);
    if (score < 0) return false;
    return !siblings.some((sibling) => navMatchScore(navLocation, sibling.url) > score);
}

/** Shared matcher so the parent's auto-open and the child's active styling agree. */
function hasActiveChild(item: INavGroupLike, navLocation: NavLocation): boolean {
    return activeNavUrl(item.items, navLocation) !== undefined;
}

/** Every collapsible group in the tree, at either depth. */
function collectGroups(items: NavItem[]): INavGroupLike[] {
    return items.flatMap((item) => {
        if (isGroupItem(item)) return [item];
        if (!item.isSection) return [];
        return (item.items ?? []).filter(isNestedGroup);
    });
}

export function NavMain({ items }: { items: NavItem[] }) {
    const { pathname, search } = useLocation();
    // `search` too: "My deals" differs from "All deals" only by `?mine=1`.
    const navLocation = useMemo<NavLocation>(() => ({ pathname, search }), [pathname, search]);
    const { isMobile, setOpenMobile, state } = useSidebar();

    // The group whose children the current route lives under, if any. Drives
    // auto-open, and wins over the stored key on first render.
    const activeGroupKey = useMemo(() => {
        const match = collectGroups(items).find((group) => hasActiveChild(group, navLocation));
        return match ? groupKeyOf(match) : null;
    }, [items, navLocation]);

    // Accordion state lives here, not in each NavGroupItem: one open key is the
    // only way opening a group can close its siblings.
    const [openGroupKey, setOpenGroupKey] = useState<string | null>(() => {
        if (activeGroupKey) return activeGroupKey;
        const stored = readStoredNavState(OPEN_GROUP_STORAGE_KEY);
        if (!stored) return null;
        // A stored key can outlive its group (permission filtering, renamed nav).
        return collectGroups(items).some((group) => groupKeyOf(group) === stored) ? stored : null;
    });

    // Navigating into a group's child must reveal it. Keyed on the value rather
    // than the render: with one shared key, re-running while `activeGroupKey` is
    // merely still-true would fight a manual toggle of a different group.
    useEffect(() => {
        if (activeGroupKey === null) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOpenGroupKey(activeGroupKey);
    }, [activeGroupKey]);

    // Only manual toggles persist — matching the previous behaviour, where the
    // route-driven open never wrote to storage.
    const handleGroupOpenChange = useCallback((key: string, next: boolean): void => {
        const value = next ? key : null;
        setOpenGroupKey(value);
        writeStoredNavState(OPEN_GROUP_STORAGE_KEY, value ?? '');
    }, []);

    // Function to handle navigation clicks
    const handleNavClick = () => {
        // Only close the sidebar on mobile devices
        if (isMobile) {
            setOpenMobile(false);
        }
    };

    return (
        <>
            {items.map((item, idx) => {
                const isActive = isNavRowActive(navLocation, item.url, items);

                if (isGroupItem(item)) {
                    const key = groupKeyOf(item);
                    return (
                        <NavGroupItem
                            key={item.title}
                            item={item}
                            navLocation={navLocation}
                            sidebarState={state}
                            isOpen={openGroupKey === key}
                            onOpenChange={(next) => handleGroupOpenChange(key, next)}
                            onNavClick={handleNavClick}
                        />
                    );
                }

                // If there are no sub-items, render a simple menu button
                if (!item.items?.length) {
                    return (
                        <SidebarGroup key={item.title} className="p-0">
                            <SidebarMenu>
                                <SidebarMenuItem
                                    className={cn(state === 'collapsed' ? 'px-2' : 'px-1')}
                                >
                                    <SidebarMenuButton
                                        asChild
                                        tooltip={item.title}
                                        isActive={isActive}
                                        size="lg"
                                        className={cn(
                                            'text-base relative border border-transparent hover:bg-sidebar-primary/15',
                                            state === 'collapsed' ? 'justify-center' : 'pl-5',
                                            isActive
                                                ? 'border-sidebar-primary/25 bg-sidebar-primary/5! before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:rounded-r before:bg-sidebar-primary'
                                                : '',
                                        )}
                                    >
                                        <Link
                                            to={item?.url || ''}
                                            className={cn(
                                                'flex w-full items-center gap-2',
                                                state === 'collapsed' && 'justify-center',
                                            )}
                                            onClick={handleNavClick}
                                        >
                                            {item.icon && (
                                                <item.icon
                                                    className={cn(
                                                        'size-5 group-data-[collapsible=icon]:size-4',
                                                        isActive
                                                            ? 'text-sidebar-primary group-hover:text-current'
                                                            : '',
                                                    )}
                                                />
                                            )}
                                            <span className={state === 'collapsed' ? 'hidden' : ''}>
                                                {item.title}
                                            </span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroup>
                    );
                }

                // Sections render with a label; collapsible ones fold their items.
                return (
                    <NavSection
                        key={item.title}
                        item={item}
                        idx={idx}
                        navLocation={navLocation}
                        sidebarState={state}
                        openGroupKey={openGroupKey}
                        onGroupOpenChange={handleGroupOpenChange}
                        onNavClick={handleNavClick}
                    />
                );
            })}
        </>
    );
}

interface NavSectionProps {
    item: NavItem;
    idx: number;
    navLocation: NavLocation;
    sidebarState: 'expanded' | 'collapsed';
    /** Accordion state, threaded through so nested groups join the same one. */
    openGroupKey: string | null;
    onGroupOpenChange: (key: string, next: boolean) => void;
    onNavClick: () => void;
}

function NavSection({
    item,
    idx,
    navLocation,
    sidebarState,
    openGroupKey,
    onGroupOpenChange,
    onNavClick,
}: NavSectionProps) {
    const storageKey = item.collapseKey ? `nav-collapsed:${item.collapseKey}` : null;
    const [open, setOpen] = useState<boolean>(() => {
        if (!storageKey) return true;
        // Sections fold independently of the NavGroupItem accordion — they are
        // labelled groupings, not menu rows — so they keep the per-key
        // `nav-collapsed:` namespace to themselves and default OPEN.
        return readStoredNavState(storageKey) !== 'closed';
    });

    const handleOpenChange = (next: boolean): void => {
        setOpen(next);
        if (storageKey) writeStoredNavState(storageKey, next ? 'open' : 'closed');
    };

    const content = (
        <SidebarGroupContent>
            <SidebarMenu>
                {(item.items ?? []).map((subItem) => {
                    // A section child can itself be a collapsible group (e.g.
                    // "Campaigns" under MARKETING). Hand it to the same
                    // component that draws top-level groups so it joins the one
                    // shared accordion rather than folding independently.
                    if (isNestedGroup(subItem)) {
                        const key = groupKeyOf(subItem);
                        return (
                            <NavGroupItem
                                key={subItem.title}
                                item={subItem}
                                navLocation={navLocation}
                                sidebarState={sidebarState}
                                isOpen={openGroupKey === key}
                                onOpenChange={(next) => onGroupOpenChange(key, next)}
                                onNavClick={onNavClick}
                            />
                        );
                    }

                    return (
                        <NavSectionRow
                            key={subItem.title}
                            subItem={subItem}
                            siblings={item.items ?? []}
                            navLocation={navLocation}
                            sidebarState={sidebarState}
                            onNavClick={onNavClick}
                        />
                    );
                })}
            </SidebarMenu>
        </SidebarGroupContent>
    );

    if (!item.collapseKey) {
        return (
            <SidebarGroup className={cn('p-0', idx > 0 && 'mt-2')}>
                <SidebarGroupLabel
                    className={cn(sidebarState === 'collapsed' ? 'pointer-events-none pl-0' : '')}
                >
                    {item.title}
                </SidebarGroupLabel>
                {content}
            </SidebarGroup>
        );
    }

    // Collapsible section (e.g. the dynamic "Modules" group). In icon-collapsed
    // sidebar mode the fold control is pointless — always show the items.
    return (
        <Collapsible
            open={sidebarState === 'collapsed' ? true : open}
            onOpenChange={handleOpenChange}
        >
            <SidebarGroup className={cn('p-0', idx > 0 && 'mt-2')}>
                {sidebarState === 'collapsed' ? (
                    <SidebarGroupLabel className="pointer-events-none pl-0">
                        {item.title}
                    </SidebarGroupLabel>
                ) : (
                    <SidebarGroupLabel asChild>
                        <CollapsibleTrigger className="flex w-full cursor-pointer items-center">
                            <span className="min-w-0 flex-1 truncate text-left">{item.title}</span>
                            <ChevronDown
                                className={cn(
                                    'size-3.5 shrink-0 text-muted-foreground transition-transform',
                                    !open && '-rotate-90',
                                )}
                            />
                        </CollapsibleTrigger>
                    </SidebarGroupLabel>
                )}
                <CollapsibleContent>{content}</CollapsibleContent>
            </SidebarGroup>
        </Collapsible>
    );
}

interface NavSectionRowProps {
    subItem: NavSubItem;
    /** The rows beside this one, so an ancestor url yields to a deeper sibling. */
    siblings: readonly NavSubItem[];
    navLocation: NavLocation;
    sidebarState: 'expanded' | 'collapsed';
    onNavClick: () => void;
}

/**
 * One navigable row inside a labelled section.
 *
 * Extracted from `NavSection` when CRM gained static sub-headings: rows appear
 * both directly under a section and nested under a heading, and both must look
 * and behave identically. Sharing the component is what guarantees that rather
 * than two copies drifting.
 */
function NavSectionRow({
    subItem,
    siblings,
    navLocation,
    sidebarState,
    onNavClick,
}: NavSectionRowProps) {
    const subUrl = subItem.url ?? '#';
    const isSubItemActive = isNavRowActive(navLocation, subItem.url, siblings);
    const hasBadge = !!subItem.badge;
    return (
        <SidebarMenuItem className={cn(sidebarState === 'collapsed' ? 'px-2' : 'px-0.5')}>
            <SidebarMenuButton
                asChild
                tooltip={subItem.title}
                isActive={isSubItemActive}
                size="lg"
                className={cn(
                    'text-base relative hover:bg-sidebar-primary/5',
                    sidebarState === 'collapsed' ? 'justify-center' : 'pl-5',
                    isSubItemActive ? 'font-medium' : '',
                    isSubItemActive
                        ? 'before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:rounded-r before:bg-sidebar-primary'
                        : '',
                    hasBadge ? 'pointer-events-none opacity-60' : '',
                )}
            >
                <Link
                    to={hasBadge ? '#' : subUrl}
                    className={cn(
                        'flex w-full items-center gap-2',
                        sidebarState === 'collapsed' && 'justify-center',
                    )}
                    onClick={(e) => {
                        if (hasBadge) {
                            e.preventDefault();
                            return;
                        }
                        onNavClick();
                    }}
                >
                    {subItem.icon && (
                        <subItem.icon
                            className={cn(
                                'size-5 group-data-[collapsible=icon]:size-4',
                                isSubItemActive
                                    ? 'text-sidebar-primary group-hover:text-current'
                                    : '',
                            )}
                            style={
                                subItem.iconColor && !isSubItemActive
                                    ? { color: subItem.iconColor }
                                    : undefined
                            }
                        />
                    )}
                    <span className={sidebarState === 'collapsed' ? 'hidden' : ''}>
                        {subItem.title}
                    </span>
                    {hasBadge && sidebarState !== 'collapsed' && (
                        <Badge
                            variant="outline"
                            className="ml-auto text-[10px] px-1.5 py-0 font-normal"
                        >
                            {subItem.badge}
                        </Badge>
                    )}
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}

interface NavGroupItemProps {
    /** A top-level group or a section-nested one — see {@link INavGroupLike}. */
    item: INavGroupLike;
    navLocation: NavLocation;
    sidebarState: 'expanded' | 'collapsed';
    /** Accordion state — owned by {@link NavMain}, which allows only one open group. */
    isOpen: boolean;
    onOpenChange: (next: boolean) => void;
    onNavClick: () => void;
}

/**
 * Collapsible menu group: a parent row that folds its children in a
 * SidebarMenuSub. Fully controlled — `NavMain` holds the single open key, so
 * opening one group closes its siblings; it also handles route auto-open and
 * persistence. In icon-collapsed sidebar mode the group becomes a dropdown so
 * children stay reachable (Radix already gives one-at-a-time there, and that
 * branch deliberately ignores `isOpen`).
 */
function NavGroupItem({
    item,
    navLocation,
    sidebarState,
    isOpen,
    onOpenChange,
    onNavClick,
}: NavGroupItemProps) {
    const isChildActive = hasActiveChild(item, navLocation);
    // Most specific match wins, so a nested route lights up one row, not two.
    const activeSubUrl = activeNavUrl(item.items, navLocation);
    const open = isOpen;

    if (sidebarState === 'collapsed') {
        return (
            <SidebarGroup className="p-0">
                <SidebarMenu>
                    <SidebarMenuItem className="px-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <SidebarMenuButton
                                    size="lg"
                                    tooltip={item.title}
                                    isActive={isChildActive}
                                    className="justify-center"
                                    aria-label={item.title}
                                >
                                    {item.icon && <item.icon className="size-4" />}
                                </SidebarMenuButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" align="start">
                                <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
                                {(item.items ?? []).map((sub) => (
                                    <DropdownMenuItem
                                        key={sub.title}
                                        asChild
                                        disabled={!!sub.badge}
                                    >
                                        <Link
                                            to={sub.badge ? '#' : (sub.url ?? '#')}
                                            onClick={(e) => {
                                                if (sub.badge) {
                                                    e.preventDefault();
                                                    return;
                                                }
                                                onNavClick();
                                            }}
                                        >
                                            {sub.icon && <sub.icon className="size-4" />}
                                            <span>{sub.title}</span>
                                        </Link>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarGroup>
        );
    }

    return (
        <SidebarGroup className="p-0">
            <SidebarMenu>
                <Collapsible open={open} onOpenChange={onOpenChange}>
                    <SidebarMenuItem className="px-1">
                        <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                                size="lg"
                                className={cn(
                                    'text-base border border-transparent pl-5 hover:bg-sidebar-primary/15',
                                    isChildActive && 'font-medium',
                                    // Open or holding the active route, the parent
                                    // reads as the heading of the branch below it.
                                    (open || isChildActive) && 'text-sidebar-primary',
                                )}
                            >
                                {item.icon && <item.icon className="size-5" />}
                                <span>{item.title}</span>
                                <ChevronDown
                                    className={cn(
                                        'ml-auto size-4 transition-transform',
                                        open || isChildActive
                                            ? 'text-sidebar-primary'
                                            : 'text-muted-foreground',
                                        !open && '-rotate-90',
                                    )}
                                />
                            </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            {/* The rail hangs from the parent icon rather than
                                the primitive's default `mx-3.5`, which is unaware
                                of this row's indent and left it 15.5px adrift.
                                Parent icon spans 25–45px from the shared
                                SidebarMenuItem box (px-1 + 1px border + pl-5),
                                so its centre is 35px: ml-[30px] + translate-x-px
                                puts the 1px border-l exactly there. `mr-3.5` is
                                restated because overriding one side of `mx`
                                drops the other. */}
                            <SidebarMenuSub className="ml-[30px] mr-3.5 border-border-strong">
                                {(item.items ?? []).map((sub) => {
                                    const subUrl = sub.url ?? '#';
                                    const isSubActive = !!sub.url && sub.url === activeSubUrl;
                                    return (
                                        <SidebarMenuSubItem
                                            key={sub.title}
                                            className={cn(
                                                // Tree rail: a tick joining each row
                                                // to the group's vertical line. The
                                                // line is the `border-l` on
                                                // SidebarMenuSub, 10px (its px-2.5)
                                                // to the left of this row's edge.
                                                'before:absolute before:top-1/2 before:-left-2.5 before:h-px before:w-2.5 before:-translate-y-1/2 before:bg-border-strong',
                                                // The active row swaps its tick for a
                                                // filled node centred on the rail.
                                                isSubActive &&
                                                    'after:absolute after:top-1/2 after:-left-[14px] after:size-2 after:-translate-y-1/2 after:rounded-full after:bg-sidebar-primary',
                                            )}
                                        >
                                            <SidebarMenuSubButton
                                                asChild
                                                isActive={isSubActive}
                                                aria-disabled={!!sub.badge}
                                                className={cn(
                                                    'h-8 text-sm',
                                                    isSubActive &&
                                                        'bg-sidebar-primary/10 text-sidebar-primary',
                                                    sub.badge && 'pointer-events-none opacity-60',
                                                )}
                                            >
                                                <Link
                                                    to={sub.badge ? '#' : subUrl}
                                                    onClick={(e) => {
                                                        if (sub.badge) {
                                                            e.preventDefault();
                                                            return;
                                                        }
                                                        onNavClick();
                                                    }}
                                                >
                                                    {sub.icon && <sub.icon className="size-4" />}
                                                    <span>{sub.title}</span>
                                                    {sub.badge && (
                                                        <Badge
                                                            variant="outline"
                                                            className="ml-auto px-1.5 py-0 text-[10px] font-normal"
                                                        >
                                                            {sub.badge}
                                                        </Badge>
                                                    )}
                                                </Link>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    );
                                })}
                            </SidebarMenuSub>
                        </CollapsibleContent>
                    </SidebarMenuItem>
                </Collapsible>
            </SidebarMenu>
        </SidebarGroup>
    );
}
